import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { AppDispatch, RootState } from '../../stateStore'
import { ApiLoadingState } from '../../middleware/api/apiList'
import { ValidationError } from '../../types/commonTypes'
import newYearPopupApi from '../../middleware/api/Login/newYearPopUpApi'
import {
  NewYearPopupResponse,
  NewYearPopupState,
} from '../../types/Login/newYearPopupTypes'

const fetchShowNewYearPopup = createAsyncThunk<
  NewYearPopupResponse | undefined,
  string | number,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'newYearPopup/fetchShowNewYearPopup',
  async (employeeId: string | number, thunkApi) => {
    try {
      return await newYearPopupApi.showNewYearPopup(employeeId)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialState: NewYearPopupState = {
  isLoading: ApiLoadingState.idle,
  error: null,
  shouldShow: false,
}

const newYearPopupSlice = createSlice({
  name: 'newYearPopup',
  initialState,
  reducers: {
    clearState: (state) => {
      state.isLoading = ApiLoadingState.idle
      state.error = null
      state.shouldShow = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchShowNewYearPopup.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(fetchShowNewYearPopup.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        const { payload } = action
        // API may return { show: boolean } or boolean
        const show = typeof payload === 'boolean' ? payload : !!payload?.show
        state.shouldShow = show
      })
      .addCase(fetchShowNewYearPopup.rejected, (state, action) => {
        state.isLoading = ApiLoadingState.failed
        state.error = action.payload as ValidationError
        state.shouldShow = false
      })
  },
})

const selectIsLoading = (state: RootState): ApiLoadingState =>
  state.newYearPopup.isLoading
const selectShouldShow = (state: RootState): boolean =>
  state.newYearPopup.shouldShow
const selectError = (state: RootState): ValidationError | null =>
  state.newYearPopup.error

export const newYearPopupService = {
  fetchShowNewYearPopup,
  actions: newYearPopupSlice.actions,
  selectors: { selectIsLoading, selectShouldShow, selectError },
}

export default newYearPopupSlice.reducer
